import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import { FiltersBox, StyledDatePicker, StyledSelectField, WalletContainer, WalletText } from "../Wallet.styles";
import { AppDispatch } from "../../../store/store";
import { useWalletTransactions } from "../../../store/selectors/walletSelector";
import { fetchWalletTransactionsThunk } from "../../../store/thunk/walletThunk";
import { useServerSidePagination } from "../../../hooks/PaginationHooks";
import { ServerSidePagination } from "../../../components/common/table/ServerSidePagination";
import { Box, MenuItem, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import TransactionsTable from "./TransactionsTable";
import { EmployeeUserType } from "../../../types/employeeType";
import { employeesApi } from "../../../api/employeesApi";
import { GeneralButton } from "../../../common/ui/GeneralButton";

const breadcrumbs = [
  {
    title: "wallet.title",
    link: "wallet",
  },
  {
    title: "wallet.transactions.transactions",
    link: "wallet/transactions",
  },
];

const Transactions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const initialLoadDone = useRef(false);
  const params = useParams();
  const walletId = params.walletId;
  const transactions = useWalletTransactions();
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [filteredMode, setFilteredMode] = useState(false);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState("all");
  const [employees, setEmployees] = useState<EmployeeUserType[]>([]);

  const isFilterIncomplete = (!!startDate && !endDate) || (!startDate && !!endDate);
  useEffect(() => {
    if (!initialLoadDone.current && walletId) {
      dispatch(fetchWalletTransactionsThunk({ walletId, offset: 1, limit: 10 }));
      initialLoadDone.current = true;
    }
  }, [walletId, dispatch]);

  useEffect(() => {
    const fetchEmployees = async () => {
      const response = await employeesApi.getCompanyEmployees();
      if (response.success() && response.result) {
        setEmployees(response.result);
      }
    };
    fetchEmployees();
  }, []);

  const fetchTransactions = (options?: { start: Dayjs | null; end: Dayjs | null; userId?: string }) => {
    if (!walletId) return;
    const userIdParam = options?.userId && options.userId !== "all" ? options.userId : undefined;
    const hasDateRange = options?.start && options?.end;

    setFilteredMode(!!hasDateRange || !!userIdParam);
    dispatch(
      fetchWalletTransactionsThunk(
        hasDateRange
          ? {
            walletId,
            startDate: options.start!.format("YYYY-MM-DD"),
            endDate: options.end!.format("YYYY-MM-DD"),
            userId: userIdParam,
          }
          : {
            walletId,
            offset: 1,
            limit: 10,
            userId: userIdParam,
          }
      )
    );
  };

  const handleFilterSubmit = () => {
    fetchTransactions({ start: startDate, end: endDate, userId: selectedEmployeeId });
  };

  const handleResetFilters = (): void => {
    setStartDate(null);
    setEndDate(null);
    setFilteredMode(false);
    setSelectedEmployeeId("all");
    fetchTransactions();
  };

  const paginatedTransactions = useServerSidePagination(
    async (paginationParams) => {
      if (!walletId || filteredMode || startDate || endDate || selectedEmployeeId !== "all") {
        return { items: [], totalCount: 0 };
      }
      const result = await dispatch(fetchWalletTransactionsThunk({
        walletId,
        ...paginationParams,
      }));
      if (fetchWalletTransactionsThunk.fulfilled.match(result)) {
        return result.payload;
      }
      return { items: [], totalCount: 0 };
    },
    []
  );

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs} title={t("wallet.transactions.transactions")} />
      <WalletContainer>
        <WalletText>{t("wallet.transactions.description")}</WalletText>

        <FiltersBox>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <StyledSelectField
              select
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="all">{t("wallet.transactions.allEmployees")}</MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {`${emp.firstName} ${emp.lastName}`}
                </MenuItem>
              ))}
            </StyledSelectField>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <StyledDatePicker
                value={startDate}
                onChange={(newValue) => setStartDate(newValue as Dayjs | null)}
                label={t("label.startDate")}
                slotProps={{
                  textField: {
                    error: !!endDate && !startDate,
                  },
                }}
              />
              <StyledDatePicker
                value={endDate}
                onChange={(newValue) => setEndDate(newValue as Dayjs | null)}
                label={t("label.endDate")}
                slotProps={{
                  textField: {
                    error: !!startDate && !endDate,
                  },
                }}
              />
              <GeneralButton
                appVariant="secondary"
                onClick={handleResetFilters}
              >
                {t("buttons.reset")}
              </GeneralButton>
              <GeneralButton
                onClick={handleFilterSubmit}
                disabled={isFilterIncomplete}
              >
                {t("buttons.ok")}
              </GeneralButton>
            </Box>
          </Box>
        </FiltersBox>


        {((filteredMode || selectedEmployeeId !== "all") && transactions?.items) ? (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("wallet.transactions.transactions")}
            </Typography>
            <TransactionsTable transactions={transactions.items} />
          </Box>
        ) : (
          <>
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("wallet.transactions.transactions")}
              </Typography>
              <TransactionsTable transactions={paginatedTransactions.items} />
            </Box>
            <ServerSidePagination paginatedData={paginatedTransactions} />
          </>
        )}
      </WalletContainer>
    </>
  );
};

export default Transactions;